import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu, LogOut, LayoutDashboard, Search, Heart } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useFavorites } from "@/contexts/FavoritesContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useHomeSections } from "@/hooks/useHomeSections";
import { buildWhatsAppUrl } from "@/lib/leadSource";
import logo from "@/assets/logo.webp";

const WHATSAPP_MSG = "Olá! Vim pelo site do Atacado Óptico e quero saber as condições de atacado.";
import { useSiteAssetUrl } from "@/hooks/useSiteAssets";
import glassesIcon from "@/assets/glasses-icon.webp";
import lensIcon from "@/assets/lens-icon.webp";
import blocosIcon from "@/assets/blocos-icon.webp";
import acessoriosIcon from "@/assets/acessorios-icon.webp";
import insumosIcon from "@/assets/insumos-icon.webp";
import fornituraIcon from "@/assets/fornitura-icon.webp";
import ferramentasIcon from "@/assets/ferramentas-icon.webp";
import surfacadoIcon from "@/assets/surfacado-icon.webp";
import pedidolabIcon from "@/assets/pedidolab-home-icon.webp";
import equipamentosIcon from "@/assets/equipamentos-icon.webp";
import promocoesIcon from "@/assets/promocoes-icon.webp";
import sobmedidaIcon from "@/assets/sobmedida-icon.webp";


// ─── Category icons: pixel-accurate Figma recreation ────────────────────────
// Style: stroke only, strokeWidth 1.6, rounded caps, no fill

// 1. Menu: 4 circles in 2×2 grid
const IconMenu = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="7"  cy="7"  r="3.4" stroke="currentColor" strokeWidth="1.6"/>
    <circle cx="17" cy="7"  r="3.4" stroke="currentColor" strokeWidth="1.6"/>
    <circle cx="7"  cy="17" r="3.4" stroke="currentColor" strokeWidth="1.6"/>
    <circle cx="17" cy="17" r="3.4" stroke="currentColor" strokeWidth="1.6"/>
  </svg>
);

// 2. Óculos de grau: imagem personalizada
const IconOculosGrau = () => {
  const src = useSiteAssetUrl("icon-glasses", glassesIcon);
  return <img src={src} alt="Óculos de grau" width={36} height={20} loading="lazy" decoding="async" className="w-9 h-5 object-contain" />;
};

// 3. Lentes: imagem personalizada
const IconLentes = () => {
  const src = useSiteAssetUrl("icon-lentes", lensIcon);
  return <img src={src} alt="Lentes" width={28} height={28} loading="lazy" decoding="async" className="w-7 h-7 object-contain" />;
};

// 4. Blocos: imagem personalizada
const IconBlocos = () => {
  const src = useSiteAssetUrl("icon-blocos", blocosIcon);
  return <img src={src} alt="Blocos" width={28} height={28} loading="lazy" decoding="async" className="w-7 h-7 object-contain" />;
};

// 5. Acessórios: imagem personalizada
const IconAcessorios = () => {
  const src = useSiteAssetUrl("icon-acessorios", acessoriosIcon);
  return <img src={src} alt="Acessórios" width={28} height={28} loading="lazy" decoding="async" className="w-7 h-7 object-contain" />;
};

// 6. Insumos: imagem personalizada
const IconInsumos = () => {
  const src = useSiteAssetUrl("icon-insumos", insumosIcon);
  return <img src={src} alt="Insumos" width={28} height={28} loading="lazy" decoding="async" className="w-7 h-7 object-contain" />;
};

// 7. Fornitura: imagem personalizada
const IconFornitura = () => {
  const src = useSiteAssetUrl("icon-fornitura", fornituraIcon);
  return <img src={src} alt="Fornitura" width={28} height={28} loading="lazy" decoding="async" className="w-7 h-7 object-contain" />;
};

// 8. Ferramentas: imagem personalizada
const IconFerramentas = () => {
  const src = useSiteAssetUrl("icon-ferramentas", ferramentasIcon);
  return <img src={src} alt="Ferramentas" width={28} height={28} loading="lazy" decoding="async" className="w-7 h-7 object-contain" />;
};

// 9. Surfaçado (blocos-lab icon): imagem personalizada
const IconBlocos2 = () => {
  const src = useSiteAssetUrl("icon-surfacado", surfacadoIcon);
  return <img src={src} alt="Surfaçado" width={28} height={28} loading="lazy" decoding="async" className="w-7 h-7 object-contain" />;
};


// 11. Equipamentos: imagem personalizada
const IconEquipamentos = () => {
  const src = useSiteAssetUrl("icon-equipamentos", equipamentosIcon);
  return <img src={src} alt="Equipamentos" width={28} height={28} loading="lazy" decoding="async" className="w-7 h-7 object-contain" />;
};

// 12. Sob medida: imagem personalizada
const IconSobMedida = () => {
  const src = useSiteAssetUrl("icon-sobmedida", sobmedidaIcon);
  return <img src={src} alt="Sob medida" width={28} height={28} loading="lazy" decoding="async" className="w-7 h-7 object-contain" />;
};

// 13. Promoções: imagem personalizada
const IconPromocoes = () => {
  const src = useSiteAssetUrl("icon-promocoes", promocoesIcon);
  return <img src={src} alt="Promoções" width={28} height={28} loading="lazy" decoding="async" className="w-7 h-7 object-contain" />;
};

// 14. Simulador: lupa com lente
const IconSimulador = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="1.6"/>
    <circle cx="10.5" cy="10.5" r="2.5" stroke="currentColor" strokeWidth="1.6"/>
    <line x1="15.5" y1="15.5" x2="20.5" y2="20.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);

// 15. Surfaçado
const IconSurfacado = () => {
  const src = useSiteAssetUrl("icon-surfacado", surfacadoIcon);
  return <img src={src} alt="Surfaçado" width={28} height={28} loading="lazy" decoding="async" className="w-7 h-7 object-contain" />;
};

// 16. Pedido ao Lab
const IconPedidoLab = () => {
  const src = useSiteAssetUrl("icon-home-pedidolab", pedidolabIcon);
  return <img src={src} alt="Pedido ao Lab" width={28} height={28} loading="lazy" decoding="async" className="w-7 h-7 object-contain" />;
};

const CATEGORIES = [
  { label: "Menu",         icon: IconMenu,         to: "/catalogo" },
  { label: "Óculos de grau", icon: IconOculosGrau, to: "/catalogo?categoria=oculos" },
  { label: "Lentes",       icon: IconLentes,       to: "/catalogo?categoria=lentes" },
  { label: "Lentes de Contato", icon: IconLentes,  to: "/catalogo?categoria=lentes-contato" },
  { label: "Blocos",       icon: IconBlocos,       to: "/catalogo?categoria=blocos" },
  { label: "Acessórios",   icon: IconAcessorios,   to: "/catalogo?categoria=acessorios" },
  { label: "Insumos",      icon: IconInsumos,      to: "/catalogo?categoria=insumos" },
  { label: "Fornitura",    icon: IconFornitura,    to: "/catalogo?categoria=fornitura" },
  { label: "Ferramentas",  icon: IconFerramentas,  to: "/catalogo?categoria=ferramentas" },
  
  { label: "Equipamentos", icon: IconEquipamentos, to: "/catalogo?categoria=equipamentos" },

  { label: "Sob medida",   icon: IconSobMedida,    to: "/crie-sua-marca" },
  { label: "Promoções",    icon: IconPromocoes,    to: "/promocao", accent: true },
  { label: "Simulador",    icon: IconSimulador,    to: "/simulador-publico" },
  { label: "Surfaçado",    icon: IconSurfacado,    to: "/surfacado" },
  { label: "Pedido Lab",   icon: IconPedidoLab,    to: "/pedido-laboratorio" },
];

// ─── Helper ──────────────────────────────────────────────────────────────────

function isLinkActive(linkTo: string, pathname: string, search: string) {
  const fullPath = pathname + search;
  return linkTo.includes("?") ? fullPath.startsWith(linkTo) : pathname === linkTo;
}

const navLinks = [
  { label: "Blog", to: "/blog" },
  { label: "Para Você", to: "/para-voce" },
  { label: "Para sua Óptica", to: "/para-sua-otica" },
  { label: "Lentes Surfaçadas", to: "/surfacado" },
  { label: "Pedido ao Lab", to: "/pedido-laboratorio" },
  { label: "Crie Sua Marca", to: "/crie-sua-marca" },
];

// ─── Component ───────────────────────────────────────────────────────────────

export function Header() {
  const { user, isAdmin, signOut } = useAuth();
  const { itemCount } = useCart();
  const { count: favoriteCount } = useFavorites();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { pathname, search } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const logoUrl = useSiteAssetUrl("logo", logo, 400);
  const searchRef = useRef<HTMLInputElement>(null);
  const mobileSearchRef = useRef<HTMLInputElement>(null);
  const { data: sections } = useHomeSections();
  const promoBar = (sections?.get("promo_bar") as any) || {};

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      const t = setTimeout(() => mobileSearchRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
  }, [mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const { data: pendingCount } = useQuery({
    queryKey: ["pending-orders-count"],
    queryFn: async () => {
      const { count } = await supabase
        .from("orders")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending");
      return count ?? 0;
    },
    enabled: isAdmin,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/catalogo?busca=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
    }
  };

  return (
    <>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:bg-accent focus:text-accent-foreground focus:px-4 focus:py-2 focus:rounded-md">
        Pular para o conteúdo principal
      </a>

      <div className={`sticky top-0 z-50 ${scrolled ? "shadow-xl" : "shadow-md"} transition-shadow duration-300`}>

        {/* ── ANNOUNCEMENT BAR ── */}
        {/* Mobile: condensed single line */}
        <div className="bg-accent text-white text-xs py-1.5 md:hidden">
          <div className="container flex items-center justify-between gap-3 font-semibold tracking-wide">
            <Link to={promoBar.link || "/catalogo"} className="flex-1 min-w-0 truncate py-3 -my-1.5 hover:text-white/80 transition-colors">
              {promoBar.text || "ECONOMIZE 10% OFF COM CUPOM ATACADO10"}
            </Link>
            <Link to="/blog" className="shrink-0 px-2 py-3 -my-1.5 underline decoration-white/40 underline-offset-2 hover:text-white/80 transition-colors">
              Blog
            </Link>
          </div>
        </div>

        {/* Desktop: full bar */}
        <div className="bg-accent text-white text-xs py-2 hidden md:block">
          <div className="container flex items-center gap-4">
            <Link to={promoBar.link || "/catalogo"} className="flex-1 min-w-0 truncate font-semibold tracking-wide hover:text-white/80 transition-colors">
              {promoBar.text || "ECONOMIZE 10% OFF COM CUPOM ATACADO10"}
            </Link>
            <div className="hidden lg:flex items-center gap-4 shrink-0">
              <span className="font-semibold">Compre via WhatsApp</span>
              <a href={buildWhatsAppUrl(WHATSAPP_MSG)} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 hover:text-white/80 transition-colors font-medium">
                <svg width="13" height="13" viewBox="0 0 32 32" fill="currentColor"><path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.132 6.742 3.052 9.376L1.056 31.2l6.064-1.952A15.9 15.9 0 0 0 16.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.31 22.608c-.39 1.098-1.932 2.01-3.15 2.276-.834.178-1.924.32-5.594-1.202-4.694-1.946-7.71-6.716-7.944-7.026-.226-.31-1.846-2.462-1.846-4.694 0-2.232 1.168-3.33 1.584-3.784.39-.424.912-.6 1.2-.6.152 0 .286.008.408.014.416.018.624.042.898.694.342.814 1.176 2.874 1.278 3.082.104.21.202.478.076.768-.118.296-.22.478-.432.738-.21.258-.44.458-.652.74-.19.244-.406.506-.172.922.234.414 1.04 1.716 2.234 2.782 1.536 1.372 2.83 1.798 3.23 1.998.4.2.634.168.868-.1.242-.276 1.034-1.2 1.31-1.614.274-.414.548-.346.922-.208.378.138 2.392 1.13 2.802 1.336.41.208.682.31.782.478.098.168.098.978-.292 2.078z"/></svg>
                (31) 98501-5555
              </a>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/blog" className="hover:text-white/80 transition-colors font-semibold">Blog</Link>
              <Link to="/para-voce" className="hover:text-white/80 transition-colors font-semibold">Para Você</Link>
              <Link to="/para-sua-otica" className="hover:text-white/80 transition-colors font-semibold">Para sua Óptica</Link>
              <a href="https://facebook.com/atacadoopticomg" target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                className="hover:text-white/70 transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
              </a>
              <a href="https://instagram.com/atacado_optico" target="_blank" rel="noopener noreferrer" aria-label="Instagram @atacado_optico"
                className="hover:text-white/70 transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.8" fill="currentColor" stroke="none"/></svg>
              </a>
              <a href="https://linkedin.com/company/atacado-optico" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn Atacado Óptico"
                className="hover:text-white/70 transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
              </a>
              {user && (
                <Link to="/meus-pedidos" className="flex items-center gap-1 hover:text-white/80 transition-colors font-medium ml-3">
                  Meus Pedidos
                </Link>
              )}
              {user && (
                <Link to="/tabela-precos" className="hover:text-white/80 transition-colors font-semibold ml-3">
                  Tabela de Preços
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* ── MAIN HEADER ── */}
        <header className="bg-white border-b border-gray-100">
          <div className="container flex h-20 items-center gap-6">

            {/* Logo */}
            <Link to="/" className="shrink-0">
              <img src={logoUrl} alt="Atacado Óptico" width={200} height={56} decoding="async" className="h-14 w-auto" />
            </Link>

            {/* Search bar */}
            <form onSubmit={handleSearch} className="flex-1 max-w-2xl hidden md:flex items-center" role="search" aria-label="Buscar produtos">
              <div className="flex w-full rounded-full border border-gray-200 bg-gray-50 overflow-hidden focus-within:ring-2 focus-within:ring-accent/30 focus-within:border-accent transition-all">
                <input
                  ref={searchRef}
                  type="search"
                  inputMode="search"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="O que você precisa?"
                  className="flex-1 px-6 py-3 text-sm bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
                  aria-label="Buscar produtos"
                />
                <button type="button"
                  onClick={() => document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", ctrlKey: true, bubbles: true }))}
                  className="hidden lg:flex items-center gap-1 px-3 text-xs text-muted-foreground border-r border-gray-200 hover:text-accent transition-colors"
                  title="Busca global (Ctrl+K)"
                  aria-label="Abrir busca global"
                >
                  <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
                    Ctrl K
                  </kbd>
                </button>
                <button type="submit"
                  className="px-5 bg-accent hover:bg-accent/90 text-white transition-colors flex items-center justify-center"
                  aria-label="Buscar">
                  <Search className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </form>

            {/* Right actions */}
            <div className="flex items-center gap-1 ml-auto md:ml-0">

              {/* User */}
              {user ? (
                <div className="hidden md:flex items-center gap-1">
                  {isAdmin && (
                    <Link to="/admin" className="relative">
                      <Button variant="ghost" size="sm" className="text-foreground/70 hover:text-accent hover:bg-accent/5 flex-col h-auto min-h-11 py-1 px-2 gap-0">
                        <LayoutDashboard className="h-5 w-5" aria-hidden="true" />
                        <span className="text-[10px] font-medium">Admin</span>
                        {(pendingCount ?? 0) > 0 && (
                          <span className="absolute -top-1 -right-1 bg-destructive text-white text-[9px] rounded-full h-4 w-4 flex items-center justify-center font-bold">
                            {pendingCount}
                          </span>
                        )}
                      </Button>
                    </Link>
                  )}
                  <Link to="/minha-conta">
                    <Button variant="ghost" size="sm" className="text-foreground/70 hover:text-accent hover:bg-accent/5 flex-col h-auto min-h-11 py-1 px-2 gap-0">
                      <User className="h-5 w-5" aria-hidden="true" />
                      <span className="text-[10px] font-medium leading-tight">Minha<br/>conta</span>
                    </Button>
                  </Link>
                  <Button variant="ghost" size="sm" onClick={signOut} aria-label="Sair da conta" className="text-foreground/70 hover:text-accent hover:bg-accent/5 p-2">
                    <LogOut className="h-5 w-5" aria-hidden="true" />
                  </Button>
                </div>
              ) : (
                <Link to="/login" aria-label="Entrar ou criar conta">
                  <Button variant="ghost" size="sm" className="text-foreground/70 hover:text-accent hover:bg-accent/5 flex-col h-auto min-h-11 py-1 px-2 gap-0">
                    <User className="h-5 w-5" aria-hidden="true" />
                    <span className="text-[10px] font-medium leading-tight hidden sm:block">Entre ou<br/>Crie sua conta</span>
                    <span className="text-[10px] font-medium leading-tight sm:hidden">Entrar</span>
                  </Button>
                </Link>
              )}


              {/* Favorites */}
              <Link to="/favoritos" className="hidden md:block relative" aria-label="Lista de favoritos">
                <Button variant="ghost" size="sm" className="text-foreground/70 hover:text-accent hover:bg-accent/5 flex-col h-auto min-h-11 py-1 px-2 gap-0">
                  <div className="relative">
                    <Heart className="h-5 w-5" aria-hidden="true" />
                    {favoriteCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[9px] rounded-full h-4 w-4 flex items-center justify-center font-bold">
                        {favoriteCount}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] font-medium leading-tight">Lista de<br/>Favoritos</span>
                </Button>
              </Link>

              {/* Cart */}
              <Link to="/carrinho" className="relative" aria-label="Carrinho de orçamento">
                <Button variant="ghost" size="sm" className="text-foreground/70 hover:text-accent hover:bg-accent/5 flex-col h-auto min-h-11 py-1 px-2 gap-0">
                  <div className="relative">
                    <ShoppingCart className="h-5 w-5" aria-hidden="true" />
                    {itemCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-accent text-white text-[9px] rounded-full h-4 w-4 flex items-center justify-center font-bold">
                        {itemCount}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] font-medium leading-tight">
                    Sacola<br/>
                    <span className="text-accent">({itemCount} {itemCount === 1 ? "item" : "itens"})</span>
                  </span>
                </Button>
              </Link>

              {/* Mobile menu button */}
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild className="md:hidden">
                  <Button variant="ghost" size="icon" className="text-foreground min-h-[44px] min-w-[44px]" aria-label="Menu">
                    <Menu className="h-5 w-5" aria-hidden="true" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-white text-foreground w-72">
                  {/* Mobile search */}
                  <form onSubmit={handleSearch} className="flex mt-6 mb-4 rounded-full border border-gray-200 overflow-hidden" role="search" aria-label="Buscar produtos">
                    <input
                      ref={mobileSearchRef}
                      type="search" inputMode="search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                      placeholder="O que você precisa?"
                      className="flex-1 px-4 py-2.5 text-sm outline-none"
                      aria-label="Buscar produtos"
                    />
                    <button type="submit" className="px-3 bg-accent text-white" aria-label="Buscar">
                      <Search className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </form>

                  <nav className="flex flex-col gap-1">
                    <p className="text-[10px] font-bold uppercase text-muted-foreground tracking-widest mb-2 px-2">Categorias</p>
                    {CATEGORIES.map((cat) => (
                      <Link key={cat.to + cat.label} to={cat.to}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${cat.accent ? "text-accent font-bold" : "text-foreground/80 hover:text-accent hover:bg-accent/5"}`}>
                        <span className={cat.accent ? "text-accent" : "text-foreground/60"}><cat.icon /></span>
                        {cat.label}
                      </Link>
                    ))}
                    <hr className="my-2 border-gray-100" />
                    <p className="text-[10px] font-bold uppercase text-muted-foreground tracking-widest mb-2 px-2">Mais</p>
                    {navLinks.map(link => (
                      <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground/80 hover:text-accent hover:bg-accent/5 transition-colors">
                        {link.label}
                      </Link>
                    ))}
                    {user && (
                      <Link to="/tabela-precos" onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-accent font-semibold hover:bg-accent/5 transition-colors">
                        Tabela de Preços
                      </Link>
                    )}
                    <hr className="my-2 border-gray-100" />
                    {user ? (
                      <>
                        {isAdmin && (
                          <Link to="/admin" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-accent font-bold">
                            <LayoutDashboard className="h-4 w-4" aria-hidden="true" /> Painel Admin
                          </Link>
                        )}
                        <Link to="/minha-conta" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm text-foreground/80 hover:text-accent">Minha Conta</Link>
                        <Link to="/meus-pedidos" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm text-foreground/80 hover:text-accent">Meus Pedidos</Link>
                        <button onClick={() => { signOut(); setMobileOpen(false); }} className="text-left px-3 py-2.5 text-sm text-foreground/80 hover:text-accent">Sair</button>
                      </>
                    ) : (
                      <Link to="/login" onClick={() => setMobileOpen(false)}>
                        <Button className="w-full bg-accent text-white hover:bg-accent/90 font-semibold mt-2">Entrar</Button>
                      </Link>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </header>

        {/* ── CATEGORY ICONS NAV ── */}
        <nav className="bg-white border-b border-gray-100" aria-label="Categorias de produtos">
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-0 py-0.5 px-4 w-max mx-auto">
              {CATEGORIES.map((cat) => {
                const active = isLinkActive(cat.to, pathname, search);
                return (
                  <Link
                    key={cat.to + cat.label}
                    to={cat.to}
                    className={`flex flex-col items-center gap-1 px-3 py-3 min-w-[72px] text-center group transition-colors shrink-0 border-b-2 ${
                      active
                        ? "border-accent text-accent"
                        : cat.accent
                        ? "border-transparent text-accent"
                        : "border-transparent text-foreground/60 hover:text-accent hover:border-accent/40"
                    }`}
                    aria-label={`Ver categoria ${cat.label}`}
                  >
                    <span className="transition-transform group-hover:scale-110 duration-200">
                      <cat.icon />
                    </span>
                    <span className={`text-[10px] font-semibold whitespace-nowrap leading-tight ${cat.accent ? "text-accent" : ""}`}>
                      {cat.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>

      </div>
    </>
  );
}
